import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Markdown from 'react-markdown'
import { CButton, CModal, CModalBody, CModalHeader, CSpinner, CTooltip } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilInfo } from '@coreui/icons'
import { useManagerHelpEntry } from '../help/managerHelpMap'

const helpContentCache = new Map()

const helpMarkdownComponents = {
  h1: ({ children }) => <h4 className="mb-3 fw-bold">{children}</h4>,
  h2: ({ children }) => <h5 className="mt-3 mb-2 fw-bold">{children}</h5>,
  h3: ({ children }) => <h6 className="mt-3 mb-2 fw-bold">{children}</h6>,
  p: ({ children }) => <p className="mb-2">{children}</p>,
  ul: ({ children }) => <ul className="mb-2 ps-3">{children}</ul>,
  li: ({ children }) => <li className="mb-1">{children}</li>,
}

const ContextHelpButton = () => {
  const { pathname } = useLocation()
  const helpEntry = useManagerHelpEntry(pathname)
  const [visible, setVisible] = useState(false)
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const fetchControllerRef = useRef(null)

  useEffect(() => {
    fetchControllerRef.current?.abort()
    setVisible(false)
  }, [pathname])

  const openHelp = () => {
    setVisible(true)

    const cached = helpContentCache.get(helpEntry.url)
    if (cached) {
      setContent(cached)
      setError('')
      setLoading(false)
      return
    }

    fetchControllerRef.current?.abort()
    const controller = new AbortController()
    fetchControllerRef.current = controller

    setLoading(true)
    setError('')
    setContent('')

    fetch(helpEntry.url, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Не удалось загрузить инструкцию')
        }
        return res.text()
      })
      .then((text) => {
        helpContentCache.set(helpEntry.url, text)
        setContent(text)
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setError('Инструкция временно недоступна. Обратитесь к администратору.')
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      })
  }

  const closeHelp = () => {
    fetchControllerRef.current?.abort()
    setVisible(false)
  }

  if (!helpEntry) {
    return null
  }

  return (
    <>
      <div
        style={{
          position: 'fixed',
          right: '24px',
          bottom: '24px',
          zIndex: 1040,
        }}
      >
        <CTooltip content="Помощь по этому разделу" placement="left">
          <CButton
            color="primary"
            shape="rounded-pill"
            className="shadow"
            style={{ width: '48px', height: '48px' }}
            onClick={openHelp}
            aria-label="Помощь"
          >
            <CIcon icon={cilInfo} size="lg" />
          </CButton>
        </CTooltip>
      </div>

      <CModal visible={visible} onClose={closeHelp} size="lg" scrollable>
        <CModalHeader closeButton>
          <strong>Помощь: {helpEntry.title}</strong>
        </CModalHeader>
        <CModalBody>
          {loading && (
            <div className="text-center py-4">
              <CSpinner color="primary" />
            </div>
          )}
          {!loading && error && <p className="text-danger mb-0">{error}</p>}
          {!loading && !error && <Markdown components={helpMarkdownComponents}>{content}</Markdown>}
        </CModalBody>
      </CModal>
    </>
  )
}

export default React.memo(ContextHelpButton)
